import type { ISbStoryData, ISbStory } from 'storyblok-js-client'
import { H3Error } from 'h3'
import { useCache } from './useCache'
import { generateStoryblokPageCacheKey } from '~~/shared/utils/cache-keys'
import type { StoryblokSettings } from '~/plugins/storyblok'

const loadStoryblokPage = async <T>(fullSlug: string, settings: StoryblokSettings): Promise<ISbStoryData<T> | null> => {
  try {
    const apiResponse = await useStoryblokApi().getStory(
      fullSlug,
      {
        ...settings.apiSettings,
        resolve_relations: 'overview.featured_story',
      },
    )
    // Typescript definition for that type didn't mention that it might have an error field.
    const data = apiResponse.data as ISbStory<T>['data'] & { error?: string }
    if (data.error) {
      throw new Error(data.error)
    }
    return apiResponse.data.story as ISbStoryData<T>
  } catch (error) {
    // Expected error, that will be handled by error.vue
    if (isStoryblokError(error) && error.status === 404) {
      const hrError = new H3Error(error.message ?? '404', { cause: error })
      hrError.statusCode = error.status
      hrError.data = error.response
      throw hrError
    }
    throw new Error(`Storyblok request failed for slug "${fullSlug}"`, { cause: error })
  }
}

export const useStoryblokPage = async <T>(fullSlug: string, useCaching = true): Promise<ISbStoryData<T> | null> => {
  const settings = useStoryblokSettings()
  
  if (!useCaching) {
    return loadStoryblokPage(fullSlug, settings)
  }
  
  // When going to the webserver, the frontend will have the response from the server saved here. For all following
  // pages that the user visits, it doesn't go the webserver but directly to storyblok with the token saved in the frontend.
  const cachedResponse = await useAsyncData(fullSlug, () => useCache(generateStoryblokPageCacheKey(settings.accessToken, fullSlug), () => loadStoryblokPage(fullSlug, settings)))
  if (cachedResponse.error.value) {
    throw cachedResponse.error.value
  }
  return cachedResponse.data.value as ISbStoryData<T>
}
